import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components/macro';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  fetchNowNext,
  fetchAddToList,
  fetchWatching,
  homeFetchData,
  fetchExtraFeedHome,
  addAddToList,
  removeAddToList,
  fetchPopularList,
  fetchTrendingList,
  fetchTopPicksList,
} from '../actions';
import CardRow from './CardRow';
import ProgrammeRows from './ProgrammeRows';
import translate from './TranslateHOC';
import { programmeRowName, decodeHtml, usePrevious } from '../helpers';
import {
  NOWNEXT_RELOAD_TIME,
  CMS_PROGROW_TYPE_PROGRAMMESET,
  CMS_PROGROW_TYPE_CATEGORY,
} from '../constants';
import PageMeta from './PageMeta';
import { HeroCard as HeroCardV2 } from '../v2/components/hero/heroCard/HeroCard';
import { CardRowLoading } from '../v2/components/loading/CardRowLoading';
import RecommendedCardRow from './RecommendedCardRow';
import SectionTitle from './SectionTitle';

const Home = ({
  fetchHome,
  fetchNowNext,
  fetchExtraFeeds,
  fetchAddToList,
  fetchWatching,
  fetchPopularList,
  fetchTrendingList,
  fetchTopPicksList,
  removeAddToList,
  addAddToList,
  strings,
  auth,
  myList,
  myListLoading,
  myListError,
  watchingData,
  watchingDataLoading,
  watchingDataError,
  profileName,
  home,
  homeIsLoading,
  homesHasErrored,
  popular,
  popularLoading,
  popularError,
  trending,
  trendingLoading,
  trendingError,
  topPicks,
  topPicksLoading,
  topPicksError,
  curLang,
  progNow,
  extraFeeds,
  history,
}) => {
  const [nowNextTimer, setNowNextTimer] = useState(null);
  const prevCurLang = usePrevious(curLang);
  const prevProfileName = usePrevious(profileName);

  useEffect(() => {
    fetchHome();
    fetchNowNext();
    fetchExtraFeeds();
    if (auth) {
      fetchAddToList();
      fetchWatching();
      fetchPopularList();
      fetchTrendingList();
      fetchTopPicksList();
    }
    setNowNextTimer(setInterval(() => fetchNowNext(), NOWNEXT_RELOAD_TIME));
    window.scrollTo(0, 0);
    return () => {
      if (nowNextTimer) {
        clearInterval(nowNextTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof prevCurLang !== 'undefined' && prevCurLang !== curLang) {
      fetchNowNext(curLang);
      fetchHome();
      fetchExtraFeeds();
    }

    if (
      ((typeof prevCurLang !== 'undefined' && prevCurLang !== curLang) ||
        (typeof prevProfileName !== 'undefined' &&
          prevProfileName !== profileName)) &&
      auth
    ) {
      fetchAddToList();
      fetchWatching();
      fetchPopularList();
      fetchTrendingList();
      fetchTopPicksList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curLang, profileName, auth]);

  const toggleProgAddToList = useCallback(
    (e, id, isList) => {
      e.preventDefault();
      if (isList) {
        removeAddToList(id);
      } else {
        addAddToList(id);
      }
    },
    [removeAddToList, addAddToList]
  );

  return (
    <div className="main">
      <PageMeta title={strings.home} />
      <HeroContainer>
        <HeroCardV2
          items={home?.heroCards}
          auth={auth}
          strings={strings}
          toggleProgAddToList={toggleProgAddToList}
          myList={myList}
          loading={homeIsLoading ? true : false}
          history={history}
        />
      </HeroContainer>
      {/* Our Picks  */}
      {!homesHasErrored && progNow && extraFeeds && home?.heroCards && (
        <CardGridContainer>
          <SectionTitle>{strings.ourPicks}</SectionTitle>
          <RecommendedCardRow
            strings={strings}
            progNow={progNow}
            extraFeeds={extraFeeds}
            heroCards={home?.heroCards}
          />
        </CardGridContainer>
      )}

      {!myListError && myList && myList.length > 0 && (
        <CardGridContainer>
          <SectionTitle>
            {strings.myList}
            <SectionTitleLink>
              <Link to={`/MyS4C/MyList`}>{strings.viewAll}</Link>
            </SectionTitleLink>
          </SectionTitle>
          {myListLoading && <CardRowLoading />}
          <CardRow
            items={myList}
            itemsIsLoading={myListLoading}
            itemsHasErrored={myListError}
            itemsLimit={99}
            billingBool={true}
          />
        </CardGridContainer>
      )}

      {!topPicksError && topPicks && topPicks.length > 0 && (
        <CardGridContainer>
          <SectionTitle>{strings.topPicks}</SectionTitle>
          {topPicksLoading && <CardRowLoading />}
          <CardRow
            items={topPicks}
            itemsIsLoading={topPicksLoading}
            itemsHasErrored={topPicksError}
            itemsLimit={99}
            billingBool={true}
            seriesLevel={true}
          />
        </CardGridContainer>
      )}

      {!trendingError && trending && trending.length > 0 && (
        <CardGridContainer>
          <SectionTitle>{strings.trendingNow}</SectionTitle>
          {trendingLoading && <CardRowLoading />}
          <CardRow
            items={trending}
            itemsIsLoading={trendingLoading}
            itemsHasErrored={trendingError}
            itemsLimit={99}
            billingBool={false}
            seriesLevel={true}
          />
        </CardGridContainer>
      )}

      {!popularError && popular && popular.length > 0 && (
        <CardGridContainer>
          <SectionTitle>{strings.popularThisWeek}</SectionTitle>
          {popularLoading && <CardRowLoading />}
          <CardRow
            items={popular}
            itemsIsLoading={popularLoading}
            itemsHasErrored={popularError}
            itemsLimit={99}
            billingBool={true}
            seriesLevel={true}
          />
        </CardGridContainer>
      )}

      {!watchingDataError && watchingData && watchingData.length > 0 && (
        <CardGridContainer>
          <SectionTitle>
            {strings.watchingForProfile} {profileName}
            <SectionTitleLink>
              <Link to={`/MyS4C/Watching`}>{strings.viewAll}</Link>
            </SectionTitleLink>
          </SectionTitle>
          {watchingDataLoading && <CardRowLoading />}
          <CardRow
            items={watchingData}
            itemsIsLoading={watchingDataLoading}
            itemsHasErrored={watchingDataError}
            itemsLimit={99}
            billingBool={true}
          />
        </CardGridContainer>
      )}
      {homeIsLoading && <CardRowLoading />}
      {!homesHasErrored &&
        home &&
        home.programmeRows &&
        home.programmeRows.length > 0 &&
        home.programmeRows.map(
          (element, index) =>
            (element.type !== CMS_PROGROW_TYPE_PROGRAMMESET ||
              (element.type === CMS_PROGROW_TYPE_PROGRAMMESET &&
                element.cards &&
                element.cards.length > 0)) && (
              <section key={index}>
                <SectionTitle>
                  {decodeHtml(programmeRowName(element))}{' '}
                  {element.type === CMS_PROGROW_TYPE_CATEGORY && (
                    <SectionTitleLink>
                      <Link to={`Categories/${element.categoryId}`}>
                        {strings.viewAll}
                      </Link>
                    </SectionTitleLink>
                  )}
                </SectionTitle>
                <ProgrammeRows
                  programmeRow={element}
                  index={index}
                  homeLoading={homeIsLoading}
                  homeError={homesHasErrored}
                />
              </section>
            )
        )}
    </div>
  );
};

const HeroContainer = styled.section``;

const CardGridContainer = styled.section`
  clear: both;
  margin-left: auto;
  margin-right: auto;
  padding: 0;
  position: relative;
  z-index: 10;
`;

const SectionTitleLink = styled.span`
  font-size: 14px;
  margin-left: 1em;
  white-space: nowrap;
  a {
    padding: 2px;
    border-radius: 4px;
    &:focus-visible {
      box-shadow: ${(props) => props.theme.focusRing};
      outline: none;
    }
  }
`;

const mapStateToProps = (state) => {
  return {
    curLang: state.curLang,
    progNow: state.now.success[0],
    auth: state.auth.authenticated,
    myList: state.list.success,
    myListLoading: state.list.loading,
    myListError: state.list.error,
    profileName: state.profileName,
    watchingData: state.watchingData.success,
    watchingDataLoading: state.watchingData.loading,
    watchingDataError: state.watchingData.error,
    home: state.home.success,
    homeIsLoading: state.home.loading,
    homesHasErrored: state.home.error,
    extraFeeds: state.extraFeedHomeItems.success,
    extraFeedsErrored: state.extraFeedHomeItems.error,
    extraFeedsLoading: state.extraFeedHomeItems.loading,
    popular: state.popular.success,
    popularLoading: state.popular.loading,
    popularError: state.popular.error,
    trending: state.trending.success,
    trendingLoading: state.trending.loading,
    trendingError: state.trending.error,
    topPicks: state.topPicks.success,
    topPicksLoading: state.topPicks.loading,
    topPicksError: state.topPicks.error,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchNowNext: () => dispatch(fetchNowNext()),
    fetchAddToList: () => dispatch(fetchAddToList()),
    fetchWatching: () => dispatch(fetchWatching()),
    fetchHome: () => dispatch(homeFetchData()),
    fetchExtraFeeds: () => dispatch(fetchExtraFeedHome()),
    addAddToList: (id) => dispatch(addAddToList(id)),
    removeAddToList: (id) => dispatch(removeAddToList(id)),
    fetchPopularList: () => dispatch(fetchPopularList()),
    fetchTrendingList: () => dispatch(fetchTrendingList()),
    fetchTopPicksList: () => dispatch(fetchTopPicksList()),
  };
};

export default translate(connect(mapStateToProps, mapDispatchToProps)(Home));
